import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * One scene metadata row open at a time (plans/stac-custom-metadata.md §7.1, R8).
 *
 * Each row is an independent component instance — four sensor tables, one row per scene — so the
 * open row lives here, above them all. A row that wants to open asks the one already open to
 * close first: a clean row just collapses, a row with unsaved changes asks the user ("Discard
 * changes?") in its own place, and a row whose save is still pending refuses. Only when it has
 * closed does the new one open, so no draft is ever dropped without a prompt. Rows are told apart
 * by INSTANCE, not by item id: a late call from a destroyed row can never unregister a live one.
 */
export interface SceneMetadataRow {
  /** Close because another row wants to open. Resolves true once closed, false to stay open. */
  requestClose(): Promise<boolean>;
}

@Injectable({ providedIn: 'root' })
export class SceneMetadataUiService {
  private readonly openId = new BehaviorSubject<string | null>(null);
  private openRow: SceneMetadataRow | null = null;

  /** The open row's item id, or null when every row is collapsed. */
  readonly open$ = this.openId.asObservable();

  get open(): string | null {
    return this.openId.value;
  }

  /**
   * Open `row`, showing item `id` — after the row already open, if any, has agreed to close.
   *
   * @returns whether `row` is now the open row.
   */
  async requestOpen(id: string, row: SceneMetadataRow): Promise<boolean> {
    if (this.openRow === row) return true;
    const current = this.openRow;
    if (current && !(await current.requestClose())) return false;
    // Another row may have opened while the prompt was showing; it is asked in turn.
    if (this.openRow && this.openRow !== current && this.openRow !== row && !(await this.openRow.requestClose())) return false;
    this.openRow = row;
    this.openId.next(id);
    return true;
  }

  /** `row` has closed — collapsed, cancelled, saved or destroyed. Any other row is unaffected. */
  closed(row: SceneMetadataRow): void {
    if (this.openRow !== row) return;
    this.openRow = null;
    this.openId.next(null);
  }
}
